import React from 'react';
import { Box, Button, Drawer } from '@mui/material';
import { COMPANY } from '../../config/companyConfig';
import PageHelmet from '../SEO/PageHelmet';
import { appStoreBadge, googlePlayBadge } from '../common/promiseAssets';
import {
  detectAppStoreClient,
  getStoreUrl,
  toWebsitePath,
} from './appStoreFallback';
import styles from './AppStoreFallbackDrawer.module.css';

const BRAND = '#CF2048';
const SHEET_EASE_ENTER = 'cubic-bezier(0.32, 0.72, 0, 1)';
const SHEET_EASE_EXIT = 'cubic-bezier(0.4, 0, 1, 1)';
const SHEET_ENTER_MS = 480;
const SHEET_EXIT_MS = 320;

class AppStoreFallbackDrawer extends React.Component {
  constructor(props) {
    super(props);
    const userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent;
    this.state = {
      open: false,
      client: detectAppStoreClient(userAgent),
    };
  }

  componentDidMount() {
    this.openFrame = window.requestAnimationFrame(() => {
      this.openFrame = window.requestAnimationFrame(() => {
        this.setState({ open: true });
      });
    });
  }

  componentWillUnmount() {
    if (this.openFrame) window.cancelAnimationFrame(this.openFrame);
    if (this.exitTimer) window.clearTimeout(this.exitTimer);
  }

  handleCloseAttempt = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
  };

  handleContinueOnWebsite = () => {
    const { location, navigate } = this.props;
    this.setState({ open: false });
    const nextPath = toWebsitePath(
      location?.pathname || '/',
      location?.search || ''
    );
    this.exitTimer = window.setTimeout(() => {
      if (typeof navigate === 'function') {
        navigate(nextPath, { replace: true });
      }
    }, SHEET_EXIT_MS);
  };

  render() {
    const { open, client } = this.state;
    const storeUrl = getStoreUrl(client);

    return (
      <>
        <PageHelmet
          title="Get the YovoTrip app"
          description="Download the YovoTrip app to book flights and hotels, or continue on the website."
          noIndex
        />
        <Drawer
          anchor="bottom"
          open={open}
          onClose={this.handleCloseAttempt}
          hideBackdrop={false}
          disableEscapeKeyDown
          transitionDuration={{ enter: SHEET_ENTER_MS, exit: SHEET_EXIT_MS }}
          SlideProps={{
            easing: {
              enter: SHEET_EASE_ENTER,
              exit: SHEET_EASE_EXIT,
            },
          }}
          ModalProps={{
            keepMounted: true,
            disableEscapeKeyDown: true,
            disableAutoFocus: true,
            disableEnforceFocus: true,
          }}
          slotProps={{
            backdrop: {
              sx: {
                backgroundColor: 'rgba(32, 32, 42, 0.45)',
                transition: `opacity ${SHEET_ENTER_MS}ms ${SHEET_EASE_ENTER} !important`,
              },
              onClick: (event) => event.stopPropagation(),
            },
          }}
          PaperProps={{
            elevation: 0,
            className: styles.paper,
            sx: {
              left: 0,
              right: 0,
              marginLeft: 'auto',
              marginRight: 'auto',
            },
          }}
          sx={{ zIndex: 1500 }}
        >
          <Box className={styles.sheet} role="dialog" aria-labelledby="app-store-fallback-title">
            <div className={styles.handle} aria-hidden="true" />
            <h2 id="app-store-fallback-title" className={styles.title}>
              Get the YovoTrip app
            </h2>
            <p className={styles.subtitle}>
              Book flights and hotels faster in the app. {COMPANY.tagline}
            </p>

            <div className={styles.actions}>
              {storeUrl ? (
                <Button
                  href={storeUrl}
                  variant="contained"
                  fullWidth
                  sx={{
                    bgcolor: BRAND,
                    '&:hover': { bgcolor: '#B51C3F' },
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '1rem',
                    py: 1.35,
                    borderRadius: '12px',
                    boxShadow: 'none',
                  }}
                >
                  Get the app
                </Button>
              ) : (
                <div className={styles.badges}>
                  <a className={styles.badgeLink} href={COMPANY.apps.ios} aria-label="Download on the App Store">
                    <img className={styles.badge} src={appStoreBadge} alt="Download on the App Store" />
                  </a>
                  <a className={styles.badgeLink} href={COMPANY.apps.android} aria-label="Get it on Google Play">
                    <img className={styles.badge} src={googlePlayBadge} alt="Get it on Google Play" />
                  </a>
                </div>
              )}

              <Button
                onClick={this.handleContinueOnWebsite}
                variant="outlined"
                fullWidth
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  py: 1.2,
                  borderRadius: '12px',
                  color: '#374151',
                  borderColor: '#d1d5db',
                  '&:hover': { borderColor: '#9ca3af', bgcolor: '#fff' },
                }}
              >
                Continue on website
              </Button>
            </div>
          </Box>
        </Drawer>
      </>
    );
  }
}

export default AppStoreFallbackDrawer;
